import { z } from 'zod';

// --- Branding ---

export const updateBrandingSchema = z.object({
  companyName: z.string().min(1).max(200).optional(),
  logo: z.string().url().optional(),
  logoAlt: z.string().max(200).optional(),
  favicon: z.string().url().optional(),
  primaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  secondaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  accentColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  fontFamily: z.string().max(100).optional(),
  headingFontFamily: z.string().max(100).optional(),
  themeMode: z.enum(['light', 'dark', 'system']).optional(),
  logoDark: z.string().url().optional(),
  heroVariant: z.enum(['split-screen', 'primary', 'animated-gradient', 'minimal-text', 'video-background']).optional(),
  customCss: z.string().max(51200).optional(),
  ogImage: z.string().url().optional(),
});

export type UpdateBrandingInput = z.infer<typeof updateBrandingSchema>;

// --- Navigation ---

export const navItemSchema: z.ZodType<CmsNavItem> = z.lazy(() =>
  z.object({
    label: z.string().min(1).max(100),
    href: z.string().min(1).max(500),
    children: z.array(navItemSchema).optional(),
  }),
);

export interface CmsNavItem {
  label: string;
  href: string;
  children?: CmsNavItem[];
}

export const updateNavigationSchema = z.object({
  items: z.array(navItemSchema).optional(),
  ctaButton: z
    .object({
      label: z.string().min(1).max(100),
      href: z.string().min(1).max(500),
      variant: z.enum(['primary', 'outline']).optional(),
    })
    .optional(),
  sticky: z.boolean().optional(),
  showPhone: z.boolean().optional(),
  phone: z.string().max(30).optional(),
});

export type UpdateNavigationInput = z.infer<typeof updateNavigationSchema>;

// --- SEO ---

export const updateSeoSchema = z.object({
  title: z.string().max(200).optional(),
  description: z.string().max(500).optional(),
  ogType: z.string().max(50).optional(),
  twitterCard: z.string().max(50).optional(),
  jsonLd: z.array(z.record(z.string(), z.unknown())).optional(),
});

export type UpdateSeoInput = z.infer<typeof updateSeoSchema>;

// --- Footer ---

export const footerLinkSchema = z.object({
  label: z.string().min(1).max(100),
  href: z.string().min(1).max(500),
});

export type FooterLink = z.infer<typeof footerLinkSchema>;

export const footerColumnSchema = z.object({
  heading: z.string().min(1).max(100),
  links: z.array(footerLinkSchema),
});

export type FooterColumn = z.infer<typeof footerColumnSchema>;

export const updateFooterSchema = z.object({
  tagline: z.string().max(500).optional(),
  columns: z.array(footerColumnSchema).optional(),
  showNmls: z.boolean().optional(),
  nmlsCompanyId: z.string().max(50).optional(),
  showEqualHousing: z.boolean().optional(),
  disclaimer: z.string().max(1000).optional(),
  legalLinks: z.array(footerLinkSchema).optional(),
});

export type UpdateFooterInput = z.infer<typeof updateFooterSchema>;

// --- Portal Links ---

export const updatePortalLinksSchema = z.object({
  applyNow: z.string().max(500).optional(),
  borrowerLogin: z.string().max(500).optional(),
  partnerPortal: z.string().max(500).optional(),
  investorPortal: z.string().max(500).optional(),
});

export type UpdatePortalLinksInput = z.infer<typeof updatePortalLinksSchema>;

// --- Page Content ---

export const updatePageContentSchema = z.object({
  pageSlug: z.string().min(1).max(200),
  title: z.string().min(1).max(200).optional(),
  published: z.boolean().optional(),
  layout: z.string().max(50).optional(),
  seo: z
    .object({
      title: z.string().max(200).optional(),
      description: z.string().max(500).optional(),
      ogTitle: z.string().max(200).optional(),
      jsonLd: z.array(z.record(z.string(), z.unknown())).optional(),
    })
    .optional(),
  sections: z.array(z.record(z.string(), z.unknown())).optional(),
});

export type UpdatePageContentInput = z.infer<typeof updatePageContentSchema>;

export const createPageSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[a-z0-9-/]+$/),
  title: z.string().min(1).max(200),
  sections: z.array(z.record(z.string(), z.unknown())).default([]),
});

export type CreatePageInput = z.infer<typeof createPageSchema>;

export const deletePageSchema = z.object({
  slug: z.string().min(1).max(200),
});

export type DeletePageInput = z.infer<typeof deletePageSchema>;

// --- Asset Upload ---

export const uploadAssetSchema = z.object({
  fileName: z.string().min(1).max(255),
  contentType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml']),
  category: z.enum(['branding', 'pages', 'blog', 'testimonials', 'deals', 'team']),
});

export type UploadAssetInput = z.infer<typeof uploadAssetSchema>;

export const confirmAssetUploadSchema = z.object({
  fileKey: z.string().min(1),
});

export type ConfirmAssetUploadInput = z.infer<typeof confirmAssetUploadSchema>;
